import { useEffect, useMemo, useState } from 'react';
import { zcreditTransactionsService, type ZCreditReportDeal } from '../../../../services/zcredit-transactions/zcredit-transactions.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';

type Params = {
  opened: boolean;
  tenantId: string;
  sessionId: number | null;
  employeeSession?: string;
};

// Summary: Fetches ZCredit deals for a session on open, filters them client-side by amount substring.
export const useZCreditTransactionsController = ({ opened, tenantId, sessionId, employeeSession }: Params) => {
  const [deals, setDeals] = useState<ZCreditReportDeal[]>([]);
  const [amountSearch, setAmountSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened || !sessionId) {
      setDeals([]);
      setAmountSearch('');
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    zcreditTransactionsService
      .listForSession(tenantId, sessionId, employeeSession)
      .then((response) => {
        if (!cancelled) {
          setDeals(response.deals);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(toHebrewApiErrorMessage(err instanceof Error ? err.message : undefined));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [opened, sessionId, tenantId, employeeSession]);

  const filteredDeals = useMemo(() => {
    const trimmed = amountSearch.trim();
    const matching = trimmed
      ? deals.filter((deal) => deal.amount !== null && deal.amount.toString().includes(trimmed))
      : deals;
    return [...matching].sort((a, b) => {
      const aTime = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
      const bTime = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [deals, amountSearch]);

  return { isLoading, error, filteredDeals, amountSearch, setAmountSearch };
};
